<template>
  <div class="warehouse-page">
    <div class="nav-bar-operations">
      <img src="@/assets/logogruas.png" alt="HomePage" @click="backMainPanel" class="logo-button" />
    </div>

    <div class="page-content">
      <div class="page-header">
        <div>
          <h2>Órdenes de compra</h2>
          <p class="page-subtitle">Pedido al proveedor antes del vale de entrada · {{ totalCount }} órdenes</p>
        </div>
        <div class="header-actions">
          <button v-if="canCreate" class="action-button action-primary" @click="openCreateModal">Nueva orden</button>
          <button class="action-button" @click="backWarehouse">Volver a Almacén</button>
        </div>
      </div>

      <div class="filters-card">
        <div class="filter-group">
          <label for="filter-oc-folio">Folio</label>
          <input id="filter-oc-folio" type="text" v-model="filters.order_number" @keyup.enter="applyFilters" placeholder="OC-1" />
        </div>
        <div class="filter-group">
          <label for="filter-oc-provider">Proveedor</label>
          <input id="filter-oc-provider" type="text" v-model="filters.provider" @keyup.enter="applyFilters" placeholder="Nombre o RFC" />
        </div>
        <div class="filter-group">
          <label for="filter-oc-part">Pieza</label>
          <input id="filter-oc-part" type="text" v-model="filters.part" @keyup.enter="applyFilters" placeholder="Nombre o código" />
        </div>
        <div class="filter-group">
          <label for="filter-oc-status">Estatus</label>
          <select id="filter-oc-status" v-model="filters.status">
            <option value="">Todos</option>
            <option v-for="option in statusOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
          </select>
        </div>
        <div class="filter-group">
          <label for="filter-oc-from">Desde</label>
          <input id="filter-oc-from" type="date" v-model="filters.start_date" />
        </div>
        <div class="filter-group">
          <label for="filter-oc-to">Hasta</label>
          <input id="filter-oc-to" type="date" v-model="filters.end_date" />
        </div>
        <div class="filter-buttons">
          <button class="action-button action-primary" @click="applyFilters">Buscar</button>
          <button class="action-button" @click="clearFilters">Limpiar</button>
        </div>
      </div>

      <div class="table-card">
        <div class="table-scroll">
          <table class="data-table">
            <thead>
              <tr>
                <th>Folio</th>
                <th>Fecha</th>
                <th>Proveedor</th>
                <th>Estatus</th>
                <th>Solicita</th>
                <th>Entrega esperada</th>
                <th class="col--num">Piezas</th>
              </tr>
            </thead>
            <tbody>
              <template v-if="loading">
                <tr v-for="n in skeletonRows" :key="`skeleton-${n}`">
                  <td v-for="col in 7" :key="col"><span class="skeleton"></span></td>
                </tr>
              </template>
              <template v-else>
                <tr v-for="order in orders" :key="order.id">
                  <td>
                    <span class="barcode-pill" role="button" tabindex="0" @click="openDetail(order)" @keyup.enter="openDetail(order)">
                      {{ order.order_number }}
                    </span>
                  </td>
                  <td>{{ formatDateTime(order.created_at) }}</td>
                  <td class="cell-strong">{{ order.provider_name }}</td>
                  <td><span class="status-pill" :class="statusClass(order.status)">{{ order.status_label }}</span></td>
                  <td :class="{ 'cell-muted': !order.requested_by_name }">{{ order.requested_by_name || '—' }}</td>
                  <td :class="{ 'cell-muted': !order.expected_on }">{{ order.expected_on || '—' }}</td>
                  <td class="col--num">{{ order.items.length }}</td>
                </tr>
                <tr v-if="orders.length === 0">
                  <td colspan="7" class="empty-row">
                    <div class="empty-state">
                      <span class="empty-state__icon">🧾</span>
                      <span class="empty-state__title">No se encontraron órdenes</span>
                      <span class="empty-state__hint">Ajusta los filtros o crea una nueva orden de compra.</span>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
        <div class="pagination-container">
          <button @click="goToPage(currentPage - 1)" :disabled="currentPage === 1" aria-label="Página anterior">‹</button>
          <span
            v-for="(pageNumber, index) in pageNumbers"
            :key="`${pageNumber}-${index}`"
            :class="{ active: pageNumber === currentPage, ellipsis: pageNumber === ellipsis }"
            @click="goToPage(pageNumber)"
          >
            {{ pageNumber }}
          </span>
          <button @click="goToPage(currentPage + 1)" :disabled="currentPage >= totalPages" aria-label="Página siguiente">›</button>
          <span class="pagination-summary">{{ totalCount }} resultados</span>
        </div>
      </div>
    </div>

    <div v-if="showCreateModal" class="modal-overlay" @click.self="closeCreateModal">
      <div class="modal-card modal-card--wide" role="dialog" aria-modal="true" aria-labelledby="oc-create-title">
        <h3 id="oc-create-title">Nueva orden de compra</h3>
        <div class="modal-body">
          <div class="modal-grid">
            <div class="form-input-group">
              <label for="oc-provider">Proveedor *</label>
              <input
                id="oc-provider"
                type="text"
                v-model="providerQuery"
                autocomplete="off"
                placeholder="Buscar por nombre, RFC o teléfono"
                @input="searchProviders"
              />
              <ul v-if="providerResults.length" class="suggest-list">
                <li v-for="provider in providerResults" :key="provider.id">
                  <button type="button" @click="selectProvider(provider)">
                    {{ provider.name }}<span v-if="provider.rfc"> · {{ provider.rfc }}</span>
                  </button>
                </li>
              </ul>
              <p v-if="form.provider" class="requisition-chip">
                <span>Seleccionado: <strong>{{ form.provider_name }}</strong></span>
                <button type="button" class="mini-button" @click="clearProvider">Cambiar</button>
              </p>
              <button type="button" class="mini-button" @click="showProviderForm = !showProviderForm">
                {{ showProviderForm ? 'Ocultar alta' : 'Nuevo proveedor' }}
              </button>
            </div>
            <div v-if="showProviderForm" class="form-input-group form-input-group--full">
              <div class="modal-grid">
                <div class="form-input-group">
                  <label for="oc-new-name">Nombre del proveedor *</label>
                  <input id="oc-new-name" type="text" v-model="newProvider.name" />
                </div>
                <div class="form-input-group">
                  <label for="oc-new-contact">Contacto</label>
                  <input id="oc-new-contact" type="text" v-model="newProvider.contact_name" />
                </div>
                <div class="form-input-group">
                  <label for="oc-new-phone">Teléfono</label>
                  <input id="oc-new-phone" type="text" v-model="newProvider.phone" />
                </div>
                <div class="form-input-group">
                  <label for="oc-new-rfc">RFC</label>
                  <input id="oc-new-rfc" type="text" v-model="newProvider.rfc" />
                </div>
                <div class="form-input-group">
                  <label for="oc-new-code">Número</label>
                  <input id="oc-new-code" type="text" v-model="newProvider.provider_code" />
                </div>
                <div class="form-input-group">
                  <label for="oc-new-fax">Fax</label>
                  <input id="oc-new-fax" type="text" v-model="newProvider.fax" />
                </div>
                <div class="form-input-group form-input-group--full">
                  <label for="oc-new-address">Dirección</label>
                  <input id="oc-new-address" type="text" v-model="newProvider.address" />
                </div>
              </div>
              <button type="button" class="action-button" @click="saveProvider" :disabled="savingProvider">
                {{ savingProvider ? 'Guardando...' : 'Guardar proveedor' }}
              </button>
            </div>
            <div class="form-input-group">
              <label for="oc-requested">Solicita *</label>
              <select id="oc-requested" v-model="form.requested_by">
                <option value="">Seleccione</option>
                <option v-for="emp in employees" :key="emp.id" :value="emp.id">
                  {{ emp.employee_ident }} - {{ emp.employee_first_name }} {{ emp.employee_last_name }}
                </option>
              </select>
            </div>
            <div class="form-input-group">
              <label for="oc-approved">Aprueba</label>
              <select id="oc-approved" v-model="form.approved_by">
                <option value="">Sin aprobar</option>
                <option v-for="emp in employees" :key="`ap-${emp.id}`" :value="emp.id">
                  {{ emp.employee_ident }} - {{ emp.employee_first_name }} {{ emp.employee_last_name }}
                </option>
              </select>
            </div>
            <div class="form-input-group">
              <label for="oc-expected">Entrega</label>
              <input id="oc-expected" type="date" v-model="form.expected_on" />
            </div>
            <div class="form-input-group">
              <label for="oc-terms">Condiciones</label>
              <input id="oc-terms" type="text" v-model="form.payment_terms" placeholder="15 DIAS" />
            </div>
            <div class="form-input-group form-input-group--full">
              <label for="oc-ship">Consignatario / ship to</label>
              <input id="oc-ship" type="text" v-model="form.ship_to" placeholder="Si se deja vacío, se usa el domicilio de la empresa" />
            </div>
            <div class="form-input-group form-input-group--full">
              <label for="oc-comments">Comentarios</label>
              <textarea id="oc-comments" v-model="form.comments" rows="2"></textarea>
            </div>
          </div>

          <div class="items-section">
            <div class="items-header">
              <h4>Piezas</h4>
              <input
                type="text"
                class="part-search"
                v-model="partSearch"
                placeholder="Buscar pieza por nombre o código..."
                aria-label="Buscar pieza"
                @input="searchParts"
              />
              <button class="mini-button" type="button" @click="addItem">Agregar pieza</button>
            </div>
            <div v-for="(item, index) in form.items" :key="index" class="item-row item-row--discount">
              <select v-model="item.part" aria-label="Pieza">
                <option value="">Seleccione pieza</option>
                <option v-for="part in partOptions" :key="part.id" :value="part.id">
                  {{ part.barcode }} - {{ part.name }}
                </option>
              </select>
              <input type="number" min="0.01" step="0.01" v-model.number="item.quantity" placeholder="Cantidad" aria-label="Cantidad" />
              <input type="number" min="0" step="0.01" v-model.number="item.unit_cost" placeholder="Precio" aria-label="Precio" />
              <input type="number" min="0" max="100" step="0.01" v-model.number="item.discount_percent" placeholder="Desc. %" aria-label="Descuento" />
              <button class="mini-button mini-button--danger" type="button" aria-label="Quitar pieza" @click="form.items.splice(index, 1)">✕</button>
            </div>
            <p v-if="form.items.length === 0" class="items-empty">Agrega al menos una pieza.</p>
          </div>
        </div>
        <div class="modal-actions">
          <button class="action-button" @click="closeCreateModal">Cancelar</button>
          <button class="action-button action-primary" @click="saveOrder" :disabled="saving">
            {{ saving ? 'Guardando...' : 'Guardar orden' }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="detail" class="modal-overlay" @click.self="detail = null">
      <div class="modal-card modal-card--wide" role="dialog" aria-modal="true" aria-labelledby="oc-detail-title">
        <h3 id="oc-detail-title">Orden de compra {{ detail.order_number }}</h3>
        <div class="modal-body">
          <div class="detail-meta">
            <div>
              <span class="detail-meta__label">Estatus</span>
              <span class="detail-meta__value">
                <span class="status-pill" :class="statusClass(detail.status)">{{ detail.status_label }}</span>
              </span>
            </div>
            <div>
              <span class="detail-meta__label">Proveedor</span>
              <span class="detail-meta__value">{{ detail.provider_name }}</span>
            </div>
            <div>
              <span class="detail-meta__label">RFC</span>
              <span class="detail-meta__value">{{ detail.provider_rfc || '—' }}</span>
            </div>
            <div>
              <span class="detail-meta__label">Teléfono</span>
              <span class="detail-meta__value">{{ detail.provider_phone || '—' }}</span>
            </div>
            <div>
              <span class="detail-meta__label">Solicita</span>
              <span class="detail-meta__value">{{ detail.requested_by_name || '—' }}</span>
            </div>
            <div>
              <span class="detail-meta__label">Aprueba</span>
              <span class="detail-meta__value">{{ detail.approved_by_name || '—' }}</span>
            </div>
            <div>
              <span class="detail-meta__label">Recibe</span>
              <span class="detail-meta__value">{{ detail.received_by_name || '—' }}</span>
            </div>
            <div>
              <span class="detail-meta__label">Entrega esperada</span>
              <span class="detail-meta__value">{{ detail.expected_on || '—' }}</span>
            </div>
            <div v-if="detail.comments" class="detail-meta--full">
              <span class="detail-meta__label">Comentarios</span>
              <span class="detail-meta__value">{{ detail.comments }}</span>
            </div>
          </div>

          <h4 class="section-title">Piezas pedidas</h4>
          <table class="data-table data-table--embedded">
            <thead>
              <tr>
                <th>Código</th>
                <th>Pieza</th>
                <th class="col--num">Pedida</th>
                <th class="col--num">Recibida</th>
                <th class="col--num">Pendiente</th>
                <th class="col--num">Costo</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in detail.items" :key="item.id">
                <td>{{ item.part_barcode }}</td>
                <td class="cell-strong">{{ item.part_name }}</td>
                <td class="col--num">{{ formatNumber(item.quantity) }} {{ item.part_unit_of_measure || '' }}</td>
                <td class="col--num">{{ formatNumber(item.received_quantity) }}</td>
                <td class="col--num cell-strong">{{ formatNumber(item.remaining_quantity) }}</td>
                <td class="col--num">${{ formatNumber(item.unit_cost) }}</td>
              </tr>
            </tbody>
          </table>

          <div v-if="detail.receipts && detail.receipts.length">
            <h4 class="section-title">Vales de entrada</h4>
            <ul class="doc-list">
              <li v-for="receipt in detail.receipts" :key="receipt.id">
                <span><strong>{{ receipt.voucher_number }}</strong> · {{ formatDateTime(receipt.created_at) }}</span>
              </li>
            </ul>
          </div>

          <h4 class="section-title">Documentos</h4>
          <div class="header-actions">
            <button v-if="canEdit" type="button" class="mini-button" @click="uploadDocument('invoice')" :disabled="uploadingDocument">Subir factura</button>
            <button v-if="canEdit" type="button" class="mini-button" @click="uploadDocument('document')" :disabled="uploadingDocument">Subir otro documento</button>
          </div>
          <p v-if="documents.length === 0" class="items-empty">Todavía no hay facturas ni documentos.</p>
          <ul v-else class="doc-list">
            <li v-for="doc in documents" :key="doc.file_id">
              <button type="button" class="mini-button" @click="openDocument(doc)">
                {{ doc.title || doc.file_name || 'Documento' }}
              </button>
              <button v-if="canEdit" type="button" class="mini-button mini-button--danger" @click="deleteDocument(doc)">Quitar</button>
            </li>
          </ul>

          <div v-if="canEdit && detail.status !== 'cancelled' && detail.status !== 'received'" class="modal-grid" style="margin-top: 18px;">
            <div class="form-input-group">
              <label for="oc-detail-approved">Aprueba</label>
              <select id="oc-detail-approved" v-model="control.approved_by">
                <option value="">Sin aprobar</option>
                <option v-for="emp in employees" :key="`dap-${emp.id}`" :value="emp.id">
                  {{ emp.employee_ident }} - {{ emp.employee_first_name }} {{ emp.employee_last_name }}
                </option>
              </select>
            </div>
            <div class="form-input-group">
              <label for="oc-detail-received">Recibe</label>
              <select id="oc-detail-received" v-model="control.received_by">
                <option value="">Sin asignar</option>
                <option v-for="emp in employees" :key="`drc-${emp.id}`" :value="emp.id">
                  {{ emp.employee_ident }} - {{ emp.employee_first_name }} {{ emp.employee_last_name }}
                </option>
              </select>
            </div>
            <div class="form-input-group">
              <label for="oc-detail-expected">Entrega</label>
              <input id="oc-detail-expected" type="date" v-model="control.expected_on" />
            </div>
            <div class="form-input-group">
              <label for="oc-detail-terms">Condiciones</label>
              <input id="oc-detail-terms" type="text" v-model="control.payment_terms" />
            </div>
            <div class="form-input-group form-input-group--full">
              <label for="oc-detail-ship">Consignatario / ship to</label>
              <input id="oc-detail-ship" type="text" v-model="control.ship_to" />
            </div>
            <div class="form-input-group form-input-group--full">
              <label for="oc-detail-comments">Comentarios</label>
              <textarea id="oc-detail-comments" v-model="control.comments" rows="2"></textarea>
            </div>
          </div>
        </div>
        <div class="modal-actions">
          <button class="action-button" @click="detail = null">Cerrar</button>
          <button class="action-button" @click="printTicketCurrent">Imprimir ticket</button>
          <button class="action-button" @click="printCurrent">Imprimir</button>
          <button v-if="canEdit && detail.status === 'pending'" class="action-button action-primary" @click="saveControl('approved')" :disabled="saving">Aprobar</button>
          <button v-if="canEdit && detail.status === 'approved'" class="action-button action-primary" @click="saveControl('in_purchasing')" :disabled="saving">Enviar a compras</button>
          <button v-if="canEdit && canCancel" class="action-button" @click="saveControl('cancelled')" :disabled="saving">Cancelar</button>
          <button v-if="canEdit && detail.status !== 'cancelled' && detail.status !== 'received'" class="action-button" @click="saveControl(detail.status)" :disabled="saving">Guardar</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Swal from '@/utils/warehouseSwal'
import { printPurchaseOrder } from '@/utils/printVoucher'
import { printPurchaseOrderTicket } from '@/utils/printTicket'
import warehouseTable from '@/mixins/warehouseTable'

export default {
  name: 'WarehousePurchaseOrders',
  mixins: [warehouseTable],
  data() {
    return {
      orders: [],
      loading: false,
      saving: false,
      savingProvider: false,
      uploadingDocument: false,
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      pageSize: 20,
      filters: this.emptyFilters(),
      employees: [],
      partOptions: [],
      partSearch: '',
      partSearchTimeout: null,
      providerQuery: '',
      providerResults: [],
      providerSearchTimeout: null,
      showProviderForm: false,
      newProvider: this.emptyProvider(),
      showCreateModal: false,
      form: this.emptyForm(),
      detail: null,
      control: this.emptyControl(),
      documents: [],
      permissions: [],
      statusOptions: [
        { value: 'pending', label: 'Pendiente' },
        { value: 'approved', label: 'Aprobada' },
        { value: 'in_purchasing', label: 'En compras' },
        { value: 'partial', label: 'Parcial' },
        { value: 'received', label: 'Recibida' },
        { value: 'cancelled', label: 'Cancelada' },
      ],
    }
  },
  computed: {
    canCreate() {
      return this.hasPerm('maintenance.create_purchase_order')
    },
    canEdit() {
      return this.hasPerm('maintenance.create_purchase_order') || this.hasPerm('maintenance.update_purchase_order')
    },
    canCancel() {
      if (!this.detail) return false
      const open = ['pending', 'approved', 'in_purchasing'].includes(this.detail.status)
      const received = (this.detail.items || []).some((item) => Number(item.received_quantity) > 0)
      return open && !received
    },
  },
  created() {
    this.loadPermissions()
  },
  mounted() {
    this.fetchOrders(1)
    this.fetchEmployees()
    this.fetchPartOptions('')
  },
  methods: {
    emptyFilters() {
      return { order_number: '', provider: '', part: '', status: '', start_date: '', end_date: '' }
    },
    emptyProvider() {
      return { name: '', contact_name: '', phone: '', rfc: '', fax: '', provider_code: '', address: '' }
    },
    emptyForm() {
      return {
        provider: '',
        provider_name: '',
        requested_by: '',
        approved_by: '',
        expected_on: '',
        payment_terms: '',
        ship_to: '',
        comments: '',
        items: [{ part: '', quantity: null, unit_cost: null, discount_percent: null }],
      }
    },
    emptyControl() {
      return { approved_by: '', received_by: '', expected_on: '', payment_terms: '', ship_to: '', comments: '' }
    },
    loadPermissions() {
      try {
        this.permissions = JSON.parse(localStorage.getItem('userPermissions')) || []
      } catch (error) {
        this.permissions = []
      }
    },
    hasPerm(perm) {
      return this.permissions.includes(perm) || this.permissions.includes(`access_control.${perm}`)
    },
    backMainPanel() {
      this.$router.push('/main-panel')
    },
    backWarehouse() {
      this.$router.push('/warehouse')
    },
    statusClass(value) {
      if (value === 'received') return 'status-pill--ok'
      if (value === 'cancelled') return 'status-pill--danger'
      if (value === 'approved' || value === 'in_purchasing') return 'status-pill--info'
      return 'status-pill--warn'
    },
    formatNumber(value) {
      const num = Number(value)
      if (Number.isNaN(num)) return '0.00'
      return num.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    formatDateTime(value) {
      if (!value) return '—'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '—'
      return date.toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' })
    },
    applyFilters() {
      this.fetchOrders(1)
    },
    clearFilters() {
      this.filters = this.emptyFilters()
      this.fetchOrders(1)
    },
    goToPage(pageNumber) {
      if (typeof pageNumber !== 'number') return
      if (pageNumber < 1 || pageNumber > this.totalPages || pageNumber === this.currentPage) return
      this.fetchOrders(pageNumber)
    },
    async fetchOrders(page) {
      const apiDirectory = process.env.API_DIRECTORY
      this.loading = true
      try {
        const response = await axios.post(
          `${apiDirectory}/warehouse/get-purchase-orders-filtered?page=${page}&page_size=${this.pageSize}`,
          this.filters,
          { headers: { 'Content-Type': 'application/json' } },
        )
        this.orders = response.data.results || []
        this.totalCount = response.data.count || 0
        this.totalPages = Math.max(1, Math.ceil(this.totalCount / this.pageSize))
        this.currentPage = page
      } catch (error) {
        console.error('Failed to fetch purchase orders:', error)
        Swal.fire('Error', 'No se pudieron cargar las órdenes de compra.', 'error')
      } finally {
        this.loading = false
      }
    },
    async fetchEmployees() {
      const apiDirectory = process.env.API_DIRECTORY
      try {
        const response = await axios.get(`${apiDirectory}/warehouse/employees-for-forms`)
        this.employees = response.data.results || []
      } catch (error) {
        console.error('Failed to fetch employees:', error)
      }
    },
    async fetchPartOptions(search) {
      const apiDirectory = process.env.API_DIRECTORY
      try {
        const response = await axios.get(`${apiDirectory}/warehouse/parts-for-forms`, { params: { search } })
        this.partOptions = response.data.results || []
      } catch (error) {
        console.error('Failed to fetch parts:', error)
      }
    },
    searchParts() {
      clearTimeout(this.partSearchTimeout)
      this.partSearchTimeout = setTimeout(() => this.fetchPartOptions(this.partSearch), 300)
    },
    searchProviders() {
      clearTimeout(this.providerSearchTimeout)
      this.providerSearchTimeout = setTimeout(async () => {
        const query = this.providerQuery.trim()
        if (query.length < 2) {
          this.providerResults = []
          return
        }
        const apiDirectory = process.env.API_DIRECTORY
        try {
          const response = await axios.get(`${apiDirectory}/warehouse/providers`, { params: { search: query } })
          this.providerResults = response.data.results || []
        } catch (error) {
          console.error('Failed to fetch providers:', error)
        }
      }, 300)
    },
    selectProvider(provider) {
      this.form.provider = provider.id
      this.form.provider_name = provider.name
      this.providerQuery = provider.name
      this.providerResults = []
    },
    clearProvider() {
      this.form.provider = ''
      this.form.provider_name = ''
      this.providerQuery = ''
    },
    async saveProvider() {
      if (!this.newProvider.name || !this.newProvider.name.trim()) {
        Swal.fire('Falta información', 'El nombre del proveedor es obligatorio.', 'warning')
        return
      }
      const apiDirectory = process.env.API_DIRECTORY
      this.savingProvider = true
      try {
        const response = await axios.post(`${apiDirectory}/warehouse/create-provider`, {
          name: this.newProvider.name.trim(),
          contact_name: this.newProvider.contact_name || null,
          phone: this.newProvider.phone || null,
          rfc: this.newProvider.rfc || null,
          fax: this.newProvider.fax || null,
          provider_code: this.newProvider.provider_code || null,
          address: this.newProvider.address || null,
        }, { headers: { 'Content-Type': 'application/json' } })
        this.selectProvider(response.data)
        this.newProvider = this.emptyProvider()
        this.showProviderForm = false
      } catch (error) {
        console.error('Failed to create provider:', error)
        const message = error.response?.data?.error
          || Object.values(error.response?.data || {}).flat().join(' ')
          || 'No se pudo guardar el proveedor.'
        Swal.fire('Error', message, 'error')
      } finally {
        this.savingProvider = false
      }
    },
    openCreateModal() {
      this.form = this.emptyForm()
      this.providerQuery = ''
      this.providerResults = []
      this.showProviderForm = false
      this.newProvider = this.emptyProvider()
      this.showCreateModal = true
    },
    closeCreateModal() {
      this.showCreateModal = false
    },
    addItem() {
      this.form.items.push({ part: '', quantity: null, unit_cost: null, discount_percent: null })
    },
    async saveOrder() {
      if (!this.form.provider) {
        Swal.fire('Falta información', 'Selecciona el proveedor.', 'warning')
        return
      }
      if (!this.form.requested_by) {
        Swal.fire('Falta información', 'Selecciona quién solicita la orden.', 'warning')
        return
      }
      const items = this.form.items.filter((item) => item.part && Number(item.quantity) > 0)
      if (items.length === 0) {
        Swal.fire('Falta información', 'Agrega al menos una pieza con cantidad mayor a cero.', 'warning')
        return
      }
      const apiDirectory = process.env.API_DIRECTORY
      this.saving = true
      try {
        const response = await axios.post(`${apiDirectory}/warehouse/create-purchase-order`, {
          provider: this.form.provider,
          requested_by: this.form.requested_by,
          approved_by: this.form.approved_by || null,
          expected_on: this.form.expected_on || null,
          payment_terms: this.form.payment_terms || null,
          ship_to: this.form.ship_to || null,
          comments: this.form.comments || null,
          items: items.map((item) => ({
            part: item.part,
            quantity: item.quantity,
            unit_cost: item.unit_cost || 0,
            discount_percent: item.discount_percent || 0,
          })),
        }, { headers: { 'Content-Type': 'application/json' } })
        this.showCreateModal = false
        const result = await Swal.fire({
          title: 'Orden de compra creada',
          text: `Folio ${response.data.order_number}. ¿Deseas imprimirla?`,
          icon: 'success',
          showCancelButton: true,
          showDenyButton: true,
          confirmButtonText: 'Imprimir',
          denyButtonText: 'Imprimir ticket',
          cancelButtonText: 'Después',
        })
        if (result.isConfirmed) printPurchaseOrder(response.data)
        if (result.isDenied) printPurchaseOrderTicket(response.data)
        this.fetchOrders(1)
      } catch (error) {
        console.error('Failed to create purchase order:', error)
        const message = error.response?.data?.error || 'No se pudo crear la orden de compra.'
        Swal.fire('Error', message, 'error')
      } finally {
        this.saving = false
      }
    },
    async openDetail(order) {
      this.detail = order
      this.control = {
        approved_by: order.approved_by || '',
        received_by: order.received_by || '',
        expected_on: order.expected_on || '',
        payment_terms: order.payment_terms || '',
        ship_to: order.ship_to || '',
        comments: order.comments || '',
      }
      await this.fetchDocuments(order.id)
    },
    printCurrent() {
      printPurchaseOrder(this.detail)
    },
    printTicketCurrent() {
      printPurchaseOrderTicket(this.detail)
    },
    async saveControl(nextStatus) {
      if (!this.detail) return
      if (nextStatus === 'approved' && !this.control.approved_by) {
        Swal.fire('Falta información', 'Selecciona quién aprueba la orden.', 'warning')
        return
      }
      if (nextStatus === 'cancelled') {
        const confirm = await Swal.fire({
          title: '¿Cancelar la orden?',
          text: 'Compras ya no podrá recibirla.',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Cancelar orden',
          cancelButtonText: 'Volver',
        })
        if (!confirm.isConfirmed) return
      }
      const apiDirectory = process.env.API_DIRECTORY
      this.saving = true
      try {
        const response = await axios.put(`${apiDirectory}/warehouse/purchase-orders/${this.detail.id}/`, {
          status: nextStatus,
          approved_by: this.control.approved_by || null,
          received_by: this.control.received_by || null,
          expected_on: this.control.expected_on || null,
          payment_terms: this.control.payment_terms || null,
          ship_to: this.control.ship_to || null,
          comments: this.control.comments || null,
        }, { headers: { 'Content-Type': 'application/json' } })
        this.detail = response.data
        this.control.approved_by = response.data.approved_by || ''
        this.control.received_by = response.data.received_by || ''
        await this.fetchOrders(this.currentPage)
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          background: '#d4edda',
          title: 'Orden actualizada',
          showConfirmButton: false,
          timer: 1800,
        })
      } catch (error) {
        console.error('Failed to update purchase order:', error)
        const message = error.response?.data?.error || 'No se pudo actualizar la orden.'
        Swal.fire('Error', message, 'error')
      } finally {
        this.saving = false
      }
    },
    async fetchDocuments(orderId) {
      const apiDirectory = process.env.API_DIRECTORY
      try {
        const response = await axios.get(`${apiDirectory}/get-files/`, {
          params: { object_type: 'warehouse_purchase_order', object_id: orderId },
        })
        const files = Array.isArray(response.data) ? response.data : []
        if (!this.detail || this.detail.id !== orderId) return
        this.documents = files.map((file) => {
          const metadata = file.metadata || {}
          const mimeType = metadata.file_type || 'application/octet-stream'
          return {
            file_id: file.file_id,
            title: metadata.title || '',
            file_name: metadata.file_name || '',
            preview_url: `data:${mimeType};base64,${file.content}`,
          }
        })
      } catch (error) {
        if (!this.detail || this.detail.id !== orderId) return
        if (error?.response?.status !== 404) console.error('Failed to fetch documents:', error)
        this.documents = []
      }
    },
    async uploadDocument(category) {
      if (!this.detail) return
      try {
        const file = await new Promise((resolve) => {
          const input = document.createElement('input')
          input.type = 'file'
          input.accept = category === 'invoice' ? 'image/*,.pdf' : '*/*'
          input.onchange = (event) => resolve(event.target.files?.[0] || null)
          input.click()
        })
        if (!file) return
        const defaultTitle = category === 'invoice' ? 'Factura' : file.name
        const metadataPrompt = await Swal.fire({
          title: 'Detalles del archivo',
          html: `
            <input id="oc-file-title" class="swal2-input" placeholder="Título" value="${String(defaultTitle).replace(/"/g, '&quot;')}">
            <textarea id="oc-file-description" class="swal2-textarea" placeholder="Descripción"></textarea>
          `,
          focusConfirm: false,
          showCancelButton: true,
          confirmButtonText: 'Subir',
          cancelButtonText: 'Cancelar',
          preConfirm: () => {
            const title = document.getElementById('oc-file-title')?.value?.trim() || ''
            const description = document.getElementById('oc-file-description')?.value?.trim() || ''
            if (!title) {
              Swal.showValidationMessage('El título es obligatorio')
              return null
            }
            return { title, description }
          },
        })
        if (!metadataPrompt.isConfirmed || !metadataPrompt.value) return
        const formData = new FormData()
        formData.append('file', file)
        formData.append('object_type', 'warehouse_purchase_order')
        formData.append('object_id', String(this.detail.id))
        formData.append('object_metadata', JSON.stringify({
          category,
          title: metadataPrompt.value.title,
          description: metadataPrompt.value.description,
          file_name: file.name,
          file_type: file.type,
        }))
        const apiDirectory = process.env.API_DIRECTORY
        this.uploadingDocument = true
        await axios.post(`${apiDirectory}/upload-file`, formData)
        await this.fetchDocuments(this.detail.id)
        Swal.fire('Completado', 'Documento subido correctamente.', 'success')
      } catch (error) {
        console.error('Failed to upload document:', error)
        Swal.fire('Error', 'No se pudo subir el documento.', 'error')
      } finally {
        this.uploadingDocument = false
      }
    },
    openDocument(doc) {
      if (!doc?.preview_url) return
      window.open(doc.preview_url, '_blank')
    },
    async deleteDocument(doc) {
      const result = await Swal.fire({
        title: '¿Quitar documento?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Quitar',
        cancelButtonText: 'Cancelar',
      })
      if (!result.isConfirmed) return
      const apiDirectory = process.env.API_DIRECTORY
      try {
        await axios.post(`${apiDirectory}/delete-file/`, { blob_id: doc.file_id })
        await this.fetchDocuments(this.detail.id)
      } catch (error) {
        console.error('Failed to delete document:', error)
        Swal.fire('Error', 'No se pudo quitar el documento.', 'error')
      }
    },
  },
}
</script>

<style scoped src="@/styles/warehouse.css"></style>
